import type { QueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type { ListAs400ExportResponse, StagedExportRowState } from '@erp/rpc';

/**
 * Fold the staged state a save returned into one cached export page. Rows
 * are matched by key (their source set), so a row's whole staged overlay and
 * baselines are replaced by what the server now holds for it. Returns the
 * same object when no row on the page was saved, so a caller can tell a
 * patched page from an untouched one. Pure, exported for unit testing.
 */
export function applyStagedRowStates(
  page: ListAs400ExportResponse,
  states: readonly StagedExportRowState[],
): ListAs400ExportResponse {
  const byKey = new Map(states.map((state) => [state.key, state]));
  let patched = false;
  const rows = page.rows.map((row) => {
    const state = byKey.get(row.key);
    if (!state) return row;
    patched = true;
    const { stagedOriginalValues: _dropped, ...rest } = row;
    return {
      ...rest,
      stagedValues: state.stagedValues ?? null,
      ...(state.stagedOriginalValues !== undefined &&
        state.stagedOriginalValues !== null && {
          stagedOriginalValues: state.stagedOriginalValues,
        }),
    };
  });
  return patched ? { ...page, rows } : page;
}

/**
 * After a save, show the saved rows' staged state on every cached export
 * page of that change set without refetching: the server already told us
 * what it holds for them, and a page refetch rebuilds every row from the
 * ingest tables (p90 over two seconds on Rexel staging, GEN-2946). Pages of
 * the change set holding none of the saved rows — another worksheet, another
 * page — are only marked stale, refetching now if one is on screen (the
 * reviewer paged away while the save was in flight) and otherwise when next
 * shown. Other change sets' pages are left alone: the same product row shows
 * each review's own staged values.
 */
export function patchStagedExportRows(
  queryClient: QueryClient,
  changeSetId: string,
  states: readonly StagedExportRowState[],
): void {
  const queryKey = orpc.inventoryManagement.products.listAs400Export.key();
  for (const query of queryClient.getQueryCache().findAll({ queryKey })) {
    if (queryChangeSetId(query.queryKey) !== changeSetId) continue;
    const page = query.state.data;
    if (!isExportPage(page)) continue;
    const next = applyStagedRowStates(page, states);
    if (next === page) {
      void queryClient.invalidateQueries({
        queryKey: query.queryKey,
        exact: true,
        refetchType: 'active',
      });
    } else {
      queryClient.setQueryData(query.queryKey, next);
    }
  }
}

/**
 * The change set an export page query was asked for. oRPC keys a query as
 * `[path, { input, type }]`, so the id sits on the second element's input.
 */
function queryChangeSetId(queryKey: readonly unknown[]): string | undefined {
  const state = queryKey[1];
  if (typeof state !== 'object' || state === null || !('input' in state)) {
    return undefined;
  }
  const { input } = state;
  if (
    typeof input !== 'object' ||
    input === null ||
    !('changeSetId' in input)
  ) {
    return undefined;
  }
  return typeof input.changeSetId === 'string' ? input.changeSetId : undefined;
}

function isExportPage(data: unknown): data is ListAs400ExportResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    Array.isArray((data as { rows?: unknown }).rows)
  );
}
